// src/pages/mission/components/LoadoutPanel.jsx

import React from 'react';
import { Package, Users, Sword, Shield, Zap } from 'lucide-react';
import { getColorClasses } from '../../themeConfig';
import { getSoldierClass, formatSoldierName, getHealthPercentage } from '../Mission.utils';
import { EQUIPMENT } from '../Mission.constants';
import EquipmentSection from './EquipmentSection';

const LoadoutPanel = ({ 
  selectedSoldier, 
  loadout, 
  onUpdateLoadout, 
  primaryColor, 
  alertColor 
}) => {
  if (!selectedSoldier) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Package className="text-yellow-400" />
          Equipment Loadout
        </h2>
        <div className="text-center py-16 text-gray-500">
          <Users size={64} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg font-bold">SELECT A SOLDIER</p>
          <p className="text-sm mt-2">Choose a soldier from the roster to customize their loadout</p>
        </div>
      </div>
    );
  }

  const soldierClass = getSoldierClass(selectedSoldier);
  const healthPercent = getHealthPercentage(selectedSoldier.health, selectedSoldier.max_health);

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Package className="text-yellow-400" />
        Equipment Loadout
      </h2>

      <div className={`${getColorClasses(primaryColor, 'bg')} border ${getColorClasses(primaryColor, 'border')} rounded-lg p-4 mb-6`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold">{formatSoldierName(selectedSoldier)}</p>
            <div className="flex items-center gap-2">
              <p className="text-gray-400">{selectedSoldier.rank}</p>
              {soldierClass && (
                <span className="text-purple-400 text-sm flex items-center gap-1">
                  {soldierClass.icon} {soldierClass.name}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">COMBAT READY</p>
            <p className="text-2xl font-bold text-green-400">{healthPercent}%</p>
          </div>
        </div>
      </div>

      <EquipmentSection
        title="Primary Weapon"
        icon={Sword}
        iconColor={getColorClasses(alertColor)}
        items={EQUIPMENT.weapons}
        selectedItemId={loadout?.weapon}
        onSelectItem={(itemId) => onUpdateLoadout(selectedSoldier.id, 'weapon', itemId)}
        gridCols="grid-cols-2"
        highlightColor={alertColor}
      />

      <EquipmentSection
        title="Body Armor"
        icon={Shield}
        iconColor={getColorClasses(primaryColor)}
        items={EQUIPMENT.armor}
        selectedItemId={loadout?.armor}
        onSelectItem={(itemId) => onUpdateLoadout(selectedSoldier.id, 'armor', itemId)}
        gridCols="grid-cols-3"
        highlightColor={primaryColor}
      />

      <EquipmentSection
        title="Utility Item"
        icon={Zap}
        iconColor="text-yellow-400"
        items={EQUIPMENT.utility}
        selectedItemId={loadout?.utility}
        onSelectItem={(itemId) => onUpdateLoadout(selectedSoldier.id, 'utility', itemId)}
        gridCols="grid-cols-4"
        highlightColor={null}
      />
    </div>
  );
};

export default LoadoutPanel;